import type { SearchableAppPage } from '@/config/appNavigation'
import type {
  SearchEntityHit,
  SearchGroup,
  SearchMetadataKind,
  SearchResult,
  SearchValueHit,
} from '@features/search/models'
import type { ResultKind } from './constants'
import type { Row } from './types'

export function isMetadataKind(kind: ResultKind): kind is SearchMetadataKind {
  return kind === 'tables' || kind === 'views' || kind === 'fields'
}

export function getMetadataIdentity(kind: SearchMetadataKind, item: SearchEntityHit): string {
  return kind === 'views' ? `${item.table_id ?? ''}:${item.id}` : item.id
}

export function getRecordDataLabel(data: Record<string, unknown>, fallback: string): string {
  for (const value of Object.values(data)) {
    if (typeof value === 'string' && value.trim().length > 0) return value.trim()
    if (typeof value === 'number') return String(value)
  }
  return fallback
}

export function getRecordLabel(record: SearchResult): string {
  return getRecordDataLabel(record.data, record.id)
}

export function buildPageRow(page: SearchableAppPage): Row {
  return {
    key: `page:${page.id}`,
    kind: 'pages',
    label: page.name,
    metaTrail: [page.module],
    selection: { kind: 'page', path: page.path },
  }
}

/** A table, view, or field row; views and fields without a table can't be opened. */
export function buildMetadataRow(kind: SearchMetadataKind, item: SearchEntityHit): Row | null {
  if (kind === 'tables') {
    return {
      key: `tbl:${item.id}`,
      kind: 'tables',
      label: item.name,
      selection: { kind: 'table', tableId: item.id },
    }
  }

  if (!item.table_id) return null

  if (kind === 'views') {
    return {
      key: `view:${item.table_id}:${item.id}`,
      kind: 'views',
      label: item.name,
      metaTrail: item.table_name ? [item.table_name] : undefined,
      selection: { kind: 'view', tableId: item.table_id, viewId: item.id },
    }
  }

  return {
    key: `field:${item.id}`,
    kind: 'fields',
    label: item.name,
    metaTrail: item.table_name ? [item.table_name] : undefined,
    selection: { kind: 'field', tableId: item.table_id },
  }
}

/** Record rows of each group in order; a typed query also names the matched columns. */
export function buildRecordRows(groups: SearchGroup[], query: string): Row[] {
  return groups.flatMap((group) =>
    group.records.map((record) => ({
      key: `rec:${group.table_id}:${record.id}`,
      kind: 'records' as const,
      label: record.label || getRecordLabel(record),
      metaTrail:
        query.trim().length > 0 && record.match_columns.length
          ? [group.table_name, `matched: ${record.match_columns.join(', ')}`]
          : [group.table_name],
      selection: {
        kind: 'record' as const,
        tableId: group.table_id,
        record: { id: record.id, data: record.data },
      },
    }))
  )
}

export function buildValueRow(value: SearchValueHit, index: number): Row {
  return {
    key: `val:${value.table_id}:${value.record_id}:${value.column_name}:${index}`,
    kind: 'values',
    label: value.value,
    metaTrail: [
      value.table_name,
      value.column_name,
      getRecordDataLabel(value.data, value.record_id),
    ],
    selection: {
      kind: 'value',
      tableId: value.table_id,
      record: { id: value.record_id, data: value.data },
    },
  }
}
